import { useCallback, useState } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  getAsaasSettings,
  getMyAsaasWallet,
  listAsaasOrganizerSettings,
  updateAsaasOrganizerSettings,
  updateAsaasSettings,
  updateMyAsaasWallet,
  type AsaasOrganizerSettings,
  type MyAsaasWalletSettings,
} from "../services/asaasAdminService";

export type AsaasSettingsDraft = {
  environment: "sandbox" | "production";
  platformWalletId: string;
  splitEnabled: boolean;
  platformFeeType: "percent" | "fixed";
  platformFeeValue: number;
};

type UseAdminPaymentsParams = {
  isAdmin: boolean;
  isOrganizer: boolean;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminPayments({
  isAdmin,
  isOrganizer,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminPaymentsParams) {
  const t = siteTexts.admin;
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [asaasSettingsDraft, setAsaasSettingsDraft] =
    useState<AsaasSettingsDraft>({
      environment: "sandbox",
      platformWalletId: "",
      splitEnabled: false,
      platformFeeType: "percent",
      platformFeeValue: 0,
    });
  const [asaasOrganizers, setAsaasOrganizers] = useState<
    AsaasOrganizerSettings[]
  >([]);
  const [myAsaasWalletDraft, setMyAsaasWalletDraft] =
    useState<MyAsaasWalletSettings>({
      walletId: "",
      splitEnabled: true,
      updatedAt: null,
    });

  const loadPaymentsSettings = useCallback(async () => {
    if (!token || (!isAdmin && !isOrganizer)) return;
    setPaymentsLoading(true);
    try {
      if (isAdmin) {
        const [settings, organizers] = await Promise.all([
          getAsaasSettings(token),
          listAsaasOrganizerSettings(token),
        ]);
        setAsaasSettingsDraft({
          environment: settings.environment,
          platformWalletId: settings.platformWalletId || "",
          splitEnabled: Boolean(settings.splitEnabled),
          platformFeeType: settings.platformFeeType,
          platformFeeValue: Number(settings.platformFeeValue || 0),
        });
        setAsaasOrganizers(organizers || []);
      } else {
        const myWallet = await getMyAsaasWallet(token);
        setMyAsaasWalletDraft({
          walletId: myWallet.walletId || "",
          splitEnabled: Boolean(myWallet.splitEnabled),
          updatedAt: myWallet.updatedAt || null,
        });
      }
    } catch (_err) {
      if (isAdmin) {
        setAsaasOrganizers([]);
      } else {
        setMyAsaasWalletDraft({
          walletId: "",
          splitEnabled: true,
          updatedAt: null,
        });
      }
    } finally {
      setPaymentsLoading(false);
    }
  }, [isAdmin, isOrganizer, token]);

  const onSaveAsaasSettings = async () => {
    if (!token || !isAdmin) return;
    try {
      await updateAsaasSettings(token, {
        environment: asaasSettingsDraft.environment,
        platformWalletId: asaasSettingsDraft.platformWalletId || undefined,
        splitEnabled: asaasSettingsDraft.splitEnabled,
        platformFeeType: asaasSettingsDraft.platformFeeType,
        platformFeeValue: Number(asaasSettingsDraft.platformFeeValue || 0),
      });
      showSuccessToast(t.paymentsTab.toasts.settingsSaved);
      await loadPaymentsSettings();
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : t.paymentsTab.toasts.settingsError,
      );
    }
  };

  const onSaveOrganizerWallet = async (
    organizerId: number,
    walletId: string,
    splitEnabled: boolean,
  ) => {
    if (!token || !isAdmin) return;
    try {
      await updateAsaasOrganizerSettings(token, organizerId, {
        walletId,
        splitEnabled,
      });
      showSuccessToast(t.paymentsTab.toasts.organizerWalletSaved);
      await loadPaymentsSettings();
    } catch (_err) {
      showErrorToast(t.paymentsTab.toasts.organizerWalletError);
    }
  };

  const onSaveMyWallet = async () => {
    if (!token || !isOrganizer) return;
    try {
      await updateMyAsaasWallet(token, {
        walletId: myAsaasWalletDraft.walletId,
        splitEnabled: Boolean(myAsaasWalletDraft.splitEnabled),
      });
      showSuccessToast(t.paymentsTab.toasts.myWalletSaved);
      await loadPaymentsSettings();
    } catch (_err) {
      showErrorToast(t.paymentsTab.toasts.myWalletError);
    }
  };

  return {
    asaasOrganizers,
    asaasSettingsDraft,
    loadPaymentsSettings,
    myAsaasWalletDraft,
    onSaveAsaasSettings,
    onSaveMyWallet,
    onSaveOrganizerWallet,
    paymentsLoading,
    setAsaasOrganizers,
    setAsaasSettingsDraft,
    setMyAsaasWalletDraft,
  };
}
